import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import Input from '../components/UI/Input';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const AdminUsers = () => {
  const { isAuthenticated, isAdmin, isSuperAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', selectedRole: '' });
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [searchTerm, setSearchTerm] = useState('');

  const ROLE_SUPER_ADMIN = 'super-admin';
  const ROLE_ADMIN = 'admin';
  const ROLE_USER = 'user';

  // Ordered most senior first. Keycloak returns realm roles in arbitrary order
  // and a user can hold several at once, so everything that displays or
  // compares a role must go by this ranking, never by array position.
  const ROLE_SENIORITY = [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_USER];

  const getPrimaryRole = (roles) =>
    ROLE_SENIORITY.find(role => (roles || []).includes(role)) || '';

  const getRoleLevel = (roles) => {
    const rank = ROLE_SENIORITY.indexOf(getPrimaryRole(roles));
    return rank === -1 ? 0 : ROLE_SENIORITY.length - 1 - rank;
  };

  const getRoleLabel = (roleName) => {
    if (roleName === ROLE_SUPER_ADMIN) return 'Super Admin';
    if (roleName === ROLE_ADMIN) return 'Admin';
    if (roleName === ROLE_USER) return 'Користувач';
    return '—';
  };

  // Бек віддає ці помилки англійським текстом у `detail`
  // (app/api/user_management.py), тож зіставляємо за сталими фрагментами.
  // Повертає null, якщо помилка не про ролі — тоді показуємо загальний текст.
  const getRoleErrorMessage = (error) => {
    const detail = error?.response?.data?.detail;

    if (detail && typeof detail === 'object') {
      return detail.error === 'insufficient_permissions'
        ? 'Недостатньо прав для керування ролями.'
        : null;
    }
    if (typeof detail !== 'string') return null;

    // getRoleLabel знає не про всі ролі realm (напр. engineer), тому там,
    // де підпису немає, показуємо машинне ім'я як є.
    const named = (roleName) => {
      const label = getRoleLabel(roleName);
      return label === '—' ? roleName : label;
    };

    if (detail.includes('own roles')) {
      return 'Не можна змінювати власні ролі. Зверніться до іншого адміністратора.';
    }
    if (detail.includes('no subject')) {
      return 'Сесія втратила ідентифікатор користувача. Увійдіть у систему повторно.';
    }

    const lowRank = detail.match(/^Insufficient rank to assign role (.+)$/);
    if (lowRank) {
      return `Недостатньо прав, щоб призначити роль «${named(lowRank[1])}».`;
    }

    const notAssignable = detail.match(/^Role (.+) is not assignable$/);
    if (notAssignable) {
      return `Роль «${notAssignable[1]}» не можна призначити через цю панель.`;
    }

    const notFound = detail.match(/^Role (.+) not found$/);
    if (notFound) {
      return `Роль «${notFound[1]}» не знайдено в Keycloak.`;
    }

    return null;
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers(currentPage, limit, '');
      const userList = response.data;
      // Fetch roles for each user in the current page
      for (let usr of userList) {
        try {
          const rolesRes = await adminAPI.getUserRoles(usr.id);
          usr.realm_roles = rolesRes.data;
        } catch {
          usr.realm_roles = [];
        }
      }
      setUsers(userList);
      setTotal(response.total);
    } catch (error) {
      toast.error('Не вдалося завантажити користувачів');
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Доступ заборонено. Тільки для адміністраторів.');
      navigate('/profile');
      return;
    }
    fetchUsers();
  }, [isAuthenticated, isAdmin, navigate, fetchUsers]);

  const currentRoleLevel = isSuperAdmin ? 2 : (isAdmin ? 1 : 0);
  const visibleUsersRaw = users.filter(u => getRoleLevel(u.realm_roles || []) <= currentRoleLevel);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return visibleUsersRaw;
    const term = searchTerm.toLowerCase();
    return visibleUsersRaw.filter(u =>
      u.email?.toLowerCase().includes(term) ||
      u.firstName?.toLowerCase().includes(term) ||
      u.lastName?.toLowerCase().includes(term)
    );
  }, [visibleUsersRaw, searchTerm]);

  const handleToggleStatus = async (targetUser) => {
    const originalUsers = [...users];
    const updatedUsers = users.map(u => u.id === targetUser.id ? { ...u, enabled: !u.enabled } : u);
    setUsers(updatedUsers);
    try {
      if (targetUser.enabled) {
        await adminAPI.deactivateUser(targetUser.id);
        toast.success(`Користувача ${targetUser.email} деактивовано`);
      } else {
        await adminAPI.activateUser(targetUser.id);
        toast.success(`Користувача ${targetUser.email} активовано`);
      }
    } catch (error) {
      setUsers(originalUsers);
      toast.error('Помилка оновлення статусу');
    }
  };

  const openEditModal = (targetUser) => {
    const currentRole = getPrimaryRole(targetUser.realm_roles);
    setSelectedUser(targetUser);
    setEditForm({
      firstName: targetUser.firstName || '',
      lastName: targetUser.lastName || '',
      email: targetUser.email || '',
      selectedRole: currentRole,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    const originalUsers = [...users];
    const updatedUser = { ...selectedUser, firstName: editForm.firstName, lastName: editForm.lastName, email: editForm.email };
    const newRole = editForm.selectedRole ? [editForm.selectedRole] : [];
    const currentRole = getPrimaryRole(selectedUser.realm_roles);
    const roleChanged = newRole[0] !== currentRole;
    if (roleChanged) updatedUser.realm_roles = newRole;
    setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
    setEditModalOpen(false);
    try {
      await adminAPI.updateUser(selectedUser.id, {
        first_name: editForm.firstName || null,
        last_name: editForm.lastName || null,
        email: editForm.email || null,
      });
      if (roleChanged) {
        await adminAPI.updateUserRoles(selectedUser.id, newRole);
      }
      toast.success('Дані користувача оновлено');
      fetchUsers(); // refresh current page
    } catch (error) {
      setUsers(originalUsers);
      toast.error(getRoleErrorMessage(error) || 'Помилка оновлення даних');
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableRoles = (targetUser) => {
    if (!targetUser) return { options: [], disabled: true };
    const targetRoleLevel = getRoleLevel(targetUser.realm_roles || []);
    if (targetUser.id === user?.sub || targetRoleLevel >= currentRoleLevel) {
      return { options: [], disabled: true };
    }
    if (isSuperAdmin) {
      return {
        options: [
          { value: '', label: 'Користувач (без ролі)' },
          { value: ROLE_SUPER_ADMIN, label: 'Super Admin' },
          { value: ROLE_ADMIN, label: 'Admin' },
          { value: ROLE_USER, label: 'Користувач' },
        ],
        disabled: false,
      };
    } else if (isAdmin) {
      return {
        options: [
          { value: '', label: 'Користувач (без ролі)' },
          { value: ROLE_ADMIN, label: 'Admin' },
          { value: ROLE_USER, label: 'Користувач' },
        ],
        disabled: false,
      };
    }
    return { options: [], disabled: true };
  };

  const totalPages = Math.ceil(total / limit);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset search when page changes
  useEffect(() => {
    setSearchTerm('');
  }, [currentPage]);

  if (!isAdmin) return null;

  return (
    <div className="py-8 w-full px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Керування користувачами</h1>
        <p className="text-gray-600 mt-2">Перегляд, активація, деактивація та редагування облікових записів</p>
      </div>

      <div className="mb-6">
        <Input
          label="Пошук на поточній сторінці"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Фільтрувати за ім'ям або email..."
          className="max-w-md"
        />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Завантаження...</div>
          ) : (
            <>
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Email</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Ім'я</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Прізвище</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Статус</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Роль</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">ID</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Дії</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((targetUser) => {
                    const roleLabel = getRoleLabel(getPrimaryRole(targetUser.realm_roles));
                    return (
                      <tr key={targetUser.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{targetUser.email}</td>
                        <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">{targetUser.firstName || '—'}</td>
                        <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">{targetUser.lastName || '—'}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${targetUser.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {targetUser.enabled ? 'Активний' : 'Деактивований'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">{roleLabel}</td>
                        <td className="px-4 py-4 text-sm font-mono text-gray-500 whitespace-nowrap">{targetUser.id?.substring(0, 8)}...</td>
                        <td className="px-4 py-4 whitespace-nowrap space-x-2">
                          <Button onClick={() => handleToggleStatus(targetUser)} variant={targetUser.enabled ? 'danger' : 'primary'} size="sm">
                            {targetUser.enabled ? 'Деактивувати' : 'Активувати'}
                          </Button>
                          <Button onClick={() => openEditModal(targetUser)} variant="secondary" size="sm">
                            Редагувати
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {totalPages > 0 && (
                <div className="flex justify-center items-center px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Button variant="secondary" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={!canGoPrev}>
                      ← Попередня
                    </Button>
                    <span className="px-3 py-2 text-sm text-gray-700">
                      Сторінка {currentPage} з {totalPages}
                    </span>
                    <Button variant="secondary" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={!canGoNext}>
                      Наступна →
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Редагування користувача" size="md">
        {selectedUser && (
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4">
              <Input label="Email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required />
              <Input label="Ім'я" type="text" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
              <Input label="Прізвище" type="text" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
              {(() => {
                const { options, disabled } = getAvailableRoles(selectedUser);
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
                    <select
                      value={editForm.selectedRole}
                      onChange={(e) => setEditForm({ ...editForm, selectedRole: e.target.value })}
                      disabled={disabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red disabled:bg-gray-100"
                    >
                      {options.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                    {disabled && (
                      <p className="text-xs text-gray-500 mt-1">
                        Редагування ролі недоступне для користувачів з таким самим або вищим рівнем доступу.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button type="button" variant="secondary" onClick={() => setEditModalOpen(false)}>Скасувати</Button>
              <Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Збереження...' : 'Зберегти'}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminUsers;